/**
 * Orders Section Sub-section
 * Interactive order analytics with date range filtering, order selection, and detail view
 */

import { useState, useMemo, useEffect, useCallback, useRef, type MouseEvent } from 'react';
import { Package, RefreshCw, CheckCircle, Clock, XCircle, Activity, X, MapPin, Truck, Calendar, User, Weight, Box, Search, Download, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { QargoData, ApiStatus } from '../../types/fleetDataHub';
import { API_ENDPOINTS } from '../../config/api';
import { authenticatedFetch } from '../../utils/authenticatedFetch';
import type { TimePeriod } from '../TimePeriodSelector';
import ControlCentreKPICard from './ControlCentreKPICard';
import ControlCentrePeriodPanel, {
  type ControlCentrePeriodId,
  coerceControlCentrePeriod,
} from './ControlCentrePeriodPanel';
import KPIDataModal, { ColumnDef } from './KPIDataModal';
import ControlCentreSourceBadge from './ControlCentreSourceBadge';
import OrdersDailyKpiSection from './OrdersDailyKpiSection';
import OrdersEnteredChart from './OrdersEnteredChart';
import {
  classifyOrderStatus,
  isCancelledOrderStatus,
} from '../../utils/orderStatusBuckets';
import { ordersAnalyticsSubtitle } from '../../utils/ingestSourceLabels';
import { formatUkNumericDateTime, ymdWeekday } from '../../utils/ukTime';
import {
  applyConsignmentStatusPatch,
  placeNameForTable,
  type EnrichableOrder,
} from '../../utils/orderDetailEnrichment';
import type { EnrichRefreshResult } from '../../hooks/usePalletwaysOrderDetailEnrich';
import {
  londonDateString,
  londonLastMonthRange,
  londonMonthToDateRange,
  shiftYmd,
} from '../../utils/ordersDailyPresentation';

interface OrdersSectionProps {
  data: QargoData | null;
  apiStatus: ApiStatus | undefined;
  onFetchData: () => void | Promise<void>;
  isLoading: boolean;
  /** When provided (e.g. mock mode), use these orders instead of fetching from API */
  mockOrders?: Order[];
  /** When set, scroll to and auto-open this KPI's detail modal */
  highlightKPI?: string | null;
  /** Clear parent highlight after alert deep-link is consumed (avoids modal reopen loops) */
  onHighlightConsumed?: () => void;
  /** When set (e.g. from Fleet Alerts), sync the section time period to this value */
  initialPeriod?: TimePeriod;
  /**
   * Notify parent when the Orders period (or custom range) needs a wider truth-layer
   * fetch — local filtering alone cannot show days that were never loaded.
   */
  onHistoryWindowChange?: (
    period: TimePeriod,
    range?: { start?: string; end?: string },
  ) => void;
  /** Palletways Control Centre: hide Name column; show Customer / Origin / Destination */
  hideNameColumn?: boolean;
  /** Org-configured order sources label, e.g. "Palletways" or "Qargo" */
  orderSourceLabel?: string;
  /** Org-configured order source ids for subtitle helpers */
  orderSourceIds?: string[];
  /**
   * Palletways Control Centre: KPI cards and drill-down from stored orders_daily.
   * Also used for admin Demo mode (same wallboard, fixture data).
   */
  ordersDailyEnabled?: boolean;
  /** Admin Demo toggle — offline orders_daily fixtures. */
  demoMode?: boolean;
}


interface Order {
  id: string;
  name: string;
  status?: string;
  date?: string; // Primary date from CSV "date" column (from BigQuery)
  source?: string;
  /** Palletways tracking ID (dc_syscon) for live status refresh */
  trackingId?: string;
  created?: {
    date?: string;
    time?: string;
    timestamp?: string;
  };
  pickup?: {
    name?: string;
    address?: string;
    postcode?: string;
    date?: string;
  };
  delivery?: {
    name?: string;
    address?: string;
    postcode?: string;
    date?: string;
    time?: string;
    timestamp?: string;
  };
  weightKg?: number;
  volumeM3?: number;
  customerRef?: string;
  /** Palletways consEntered type */
  type?: string;
  /** Palletways lifts / pallet spaces */
  lifts?: number;
  /** Proof-of-delivery reference, from the confirmed OrderDelivered event */
  podReference?: string;
  /** From the confirmed OrderCancelled event */
  cancelledAt?: string;
  /** From the confirmed OrderCancelled event — normalised reason, not raw carrier text */
  cancellationReason?: string;
  /** Latest OrderArrivedAtStop — informational, does not itself imply delivery */
  lastArrivedAt?: string;
  /** Latest OrderCustodyTransferred — informational, does not itself imply delivery */
  lastCustodyTransferAt?: string;
  /** Raw operator entity id from the latest custody transfer — no label resolution yet */
  custodyOperatorEntityId?: string;
  _raw?: any;
}

// Column definitions for the KPI detail modal
const ORDER_COLUMNS: ColumnDef[] = [
  { key: 'name', header: 'Order ID', render: (v, row) => <span className="font-mono text-chart-5">{(v || row?.id || '-') as string}</span> },
  { key: 'date', header: 'Date' },
  { key: 'status', header: 'Status', render: (v) => (
    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${orderStatusPillClass(String(v || ''))}`}>
      {v || 'Unknown'}
    </span>
  )},
  { key: 'type', header: 'Type' },
  { key: 'customerRef', header: 'Customer' },
  { key: 'pickup.address', header: 'Origin' },
  { key: 'delivery.address', header: 'Destination' },
  { key: 'weightKg', header: 'Weight', align: 'right', render: (v) => v ? `${Number(v).toLocaleString()} kg` : '-' },
];

const ORDER_ID_DISPLAY_MAX = 14;

function truncateOrderId(value: string, max = ORDER_ID_DISPLAY_MAX): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max)}…`;
}

/** Hide address when it only echoes town + postcode already shown as separate lines. */
function distinctPlaceAddress(place?: { name?: string; address?: string; postcode?: string }): string | null {
  const addr = place?.address?.trim();
  if (!addr) return null;
  const name = place?.name?.trim() || '';
  const postcode = place?.postcode?.trim() || '';
  const composed = [name, postcode].filter(Boolean).join(', ');
  if (addr === name || addr === composed || (name && postcode && addr === `${name} ${postcode}`)) {
    return null;
  }
  return addr;
}

function orderStatusTextClass(statusRaw?: string): string {
  const status = (statusRaw || 'unknown').toLowerCase();
  if (status.includes('deleted')) return 'text-destructive';
  if (status.includes('at delivery depot') || status.includes('at the hub')) return 'text-ring';
  if (status.includes('complete') || status.includes('delivered')) return 'text-success';
  if (status.includes('progress') || status.includes('out for delivery')) return 'text-ring';
  if (status.includes('pending') || status.includes('planned') || status.includes('entered')) return 'text-warning';
  if (status.includes('cancel')) return 'text-destructive';
  return 'text-muted-foreground';
}

function orderStatusPillClass(statusRaw?: string): string {
  const status = (statusRaw || 'unknown').toLowerCase();
  if (status.includes('deleted')) return 'bg-destructive/20 text-destructive';
  if (status.includes('at delivery depot') || status.includes('at the hub')) return 'bg-ring/20 text-ring';
  if (status.includes('complete') || status.includes('delivered')) return 'bg-success/20 text-success';
  if (status.includes('cancel')) return 'bg-destructive/20 text-destructive';
  if (status.includes('progress') || status.includes('active') || status.includes('out for delivery')) {
    return 'bg-ring/20 text-ring';
  }
  return 'bg-accent/20 text-foreground';
}

export default function OrdersSection({ data, onFetchData, isLoading, mockOrders, highlightKPI, onHighlightConsumed, initialPeriod, onHistoryWindowChange, hideNameColumn, orderSourceLabel, orderSourceIds = [], ordersDailyEnabled = false, demoMode = false }: OrdersSectionProps) {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(
    () => coerceControlCentrePeriod(initialPeriod, 'today'),
  );
  const [customStartDate] = useState<string>('');
  const [customEndDate] = useState<string>('');
  const [orders, setOrders] = useState<Order[]>(mockOrders ?? []);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [exportDateRange, setExportDateRange] = useState<{ start: string; end: string } | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [autoSwitchedToAllTime, setAutoSwitchedToAllTime] = useState(false);
  const [isDemoUserForOrders, setIsDemoUserForOrders] = useState(false);
  /** Tracking IDs currently refreshing Palletways live status */
  const [statusRefreshingIds, setStatusRefreshingIds] = useState<Set<string>>(() => new Set());
  const [statusRefreshError, setStatusRefreshError] = useState<string | null>(null);
  /** Iceberg day rollup for Orders Entered chart (independent of ingest lookback / list window) */
  const [ordersActivityByDate, setOrdersActivityByDate] = useState<Record<
    string,
    { orders: number; completed: number; open: number }
  > | null>(null);

  // KPI detail modal state
  const [kpiModal, setKpiModal] = useState<{ open: boolean; kpi: string; title: string; subtitle?: string; filterFn?: (row: any) => boolean }>({ open: false, kpi: '', title: '' });
  const kpiGridRef = useRef<HTMLDivElement>(null);

  // Period handlers
  const handlePeriodChange = useCallback((period: ControlCentrePeriodId | TimePeriod) => {
    setSelectedPeriod(period);
    onHistoryWindowChange?.(period);
  }, [onHistoryWindowChange]);

  // Sync time period when navigated from Fleet Alerts with a specific period
  useEffect(() => {
    if (initialPeriod != null) {
      setSelectedPeriod(coerceControlCentrePeriod(initialPeriod, 'today'));
    }
  }, [initialPeriod]);

  // Auto-open KPI modal once when navigated from Control Centre alerts
  useEffect(() => {
    if (!highlightKPI) return;
    kpiGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (ordersDailyEnabled && ['collections', 'deliveries', 'active', 'completed'].includes(highlightKPI)) {
      return;
    }
    const timer = setTimeout(() => {
      const kpiMap: Record<string, { title: string; filterFn?: (row: any) => boolean }> = {
        collections: { title: 'Collections' },
        deliveries: { title: 'Deliveries' },
        cancelled: { title: 'Cancelled Orders', filterFn: (o: any) => isCancelledOrderStatus(o.status) },
      };
      const match = kpiMap[highlightKPI];
      if (match) {
        setKpiModal({ open: true, kpi: highlightKPI, title: match.title, filterFn: match.filterFn });
      }
      onHighlightConsumed?.();
    }, 400);
    return () => clearTimeout(timer);
  }, [highlightKPI, onHighlightConsumed, ordersDailyEnabled]);
  
  // Table controls
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  type SortField = 'id' | 'name' | 'status' | 'type' | 'date' | 'customerRef' | 'pickup' | 'delivery' | 'weightKg';
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(['date', 'weightKg'].includes(field) ? 'desc' : 'asc');
    }
    setCurrentPage(1);
  };

  // Period bounds use Europe/London calendar days (aligned with Live chart + §2.5).
  const getDateRange = useMemo(() => {
    const todayStr = londonDateString();

    let startDate: Date;
    let endDate: Date;

    switch (selectedPeriod) {
      case 'today':
        startDate = new Date(todayStr + 'T00:00:00.000Z');
        endDate = new Date(todayStr + 'T23:59:59.999Z');
        break;
      case 'yesterday': {
        const yesterdayStr = shiftYmd(todayStr, -1);
        startDate = new Date(yesterdayStr + 'T00:00:00.000Z');
        endDate = new Date(yesterdayStr + 'T23:59:59.999Z');
        break;
      }
      case 'last-week': {
        const sevenDaysAgoStr = shiftYmd(todayStr, -7);
        startDate = new Date(sevenDaysAgoStr + 'T00:00:00.000Z');
        endDate = new Date(todayStr + 'T23:59:59.999Z');
        break;
      }
      case 'month-to-date': {
        const { start, end } = londonMonthToDateRange();
        startDate = new Date(start + 'T00:00:00.000Z');
        endDate = new Date(end + 'T23:59:59.999Z');
        break;
      }
      case 'last-month': {
        const { start, end } = londonLastMonthRange();
        startDate = new Date(start + 'T00:00:00.000Z');
        endDate = new Date(end + 'T23:59:59.999Z');
        break;
      }
      case 'last_30_days': {
        const startStr = shiftYmd(todayStr, -30);
        startDate = new Date(startStr + 'T00:00:00.000Z');
        endDate = new Date(todayStr + 'T23:59:59.999Z');
        break;
      }
      case 'last_180_days': {
        const startStr = shiftYmd(todayStr, -180);
        startDate = new Date(startStr + 'T00:00:00.000Z');
        endDate = new Date(todayStr + 'T23:59:59.999Z');
        break;
      }
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate + 'T00:00:00.000Z');
          endDate = new Date(customEndDate + 'T23:59:59.999Z');
        } else {
          const defaultStartStr = shiftYmd(todayStr, -7);
          startDate = new Date(defaultStartStr + 'T00:00:00.000Z');
          endDate = new Date(todayStr + 'T23:59:59.999Z');
        }
        break;
      case 'all-time':
        startDate = new Date('2000-01-01T00:00:00.000Z');
        endDate = new Date('2099-12-31T23:59:59.999Z');
        break;
      default: {
        const defaultStartStr = shiftYmd(todayStr, -7);
        startDate = new Date(defaultStartStr + 'T00:00:00.000Z');
        endDate = new Date(todayStr + 'T23:59:59.999Z');
      }
    }

    return { startDate, endDate };
  }, [selectedPeriod, customStartDate, customEndDate]);

  // Fetch orders function (reusable)
  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      // Fetch orders from pre-exported CSV/JSON files (read-only, no API calls triggered)
      const url = API_ENDPOINTS.qargoOrders();
      
      console.log('📦 [PRODUCTION DEBUG] Fetching Qargo orders from:', url);
      
      const response = await authenticatedFetch(url);
      
      console.log('📦 [PRODUCTION DEBUG] Response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [PRODUCTION DEBUG] Error response body:', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText };
        }
        console.error('❌ API Error Response:', response.status, errorData);
        throw new Error(errorData.error || errorData.message || `Failed to fetch orders: ${response.status}`);
      }
      
      const responseData = await response.json();
      setIsDemoUserForOrders(Boolean(responseData?.isDemoUser));
      console.log('✅ [PRODUCTION DEBUG] Qargo Orders Response:', {
        total: responseData.total,
        ordersCount: responseData.orders?.length || 0,
        exportTimestamp: responseData.export_timestamp,
        note: responseData.note,
        source: responseData.source,
        hasOrders: Array.isArray(responseData.orders),
        responseKeys: Object.keys(responseData)
      });
      
      // Extract orders from response
      const fetchedOrders: Order[] = Array.isArray(responseData.orders) ? responseData.orders : [];
      
      console.log('📋 [PRODUCTION DEBUG] Fetched orders:', fetchedOrders.length, 'orders');
      if (fetchedOrders.length > 0) {
        console.log('📄 Sample order:', {
          id: fetchedOrders[0].id,
          name: fetchedOrders[0].name,
          status: fetchedOrders[0].status,
          pickup: fetchedOrders[0].pickup?.address,
          delivery: fetchedOrders[0].delivery?.address,
          weight: fetchedOrders[0].weightKg,
          volume: fetchedOrders[0].volumeM3,
          dates: {
            delivery: fetchedOrders[0].delivery?.date,
            pickup: fetchedOrders[0].pickup?.date,
            created: fetchedOrders[0].created?.date
          }
        });
        
        // Check how many orders have dates (including primary date field)
        const ordersWithDates = fetchedOrders.filter(o => 
          o.date || o.delivery?.date || o.pickup?.date || o.created?.date
        );
        console.log(`📅 [PRODUCTION DEBUG] Orders with dates: ${ordersWithDates.length} / ${fetchedOrders.length}`);
        console.log(`📅 [PRODUCTION DEBUG] Orders with primary date field: ${fetchedOrders.filter(o => o.date).length}`);
        console.log(`📅 [PRODUCTION DEBUG] Sample order date fields:`, fetchedOrders.length > 0 ? {
          primaryDate: fetchedOrders[0].date,
          deliveryDate: fetchedOrders[0].delivery?.date,
          pickupDate: fetchedOrders[0].pickup?.date,
          createdDate: fetchedOrders[0].created?.date
        } : 'No orders');
      } else {
        console.warn('⚠️ [PRODUCTION DEBUG] No orders in response!');
        console.warn('⚠️ [PRODUCTION DEBUG] Response structure:', JSON.stringify(responseData, null, 2).substring(0, 500));
      }
      
      setOrders(fetchedOrders);
      setExportDateRange(responseData.date_range ?? null);
      console.log('✅ [PRODUCTION DEBUG] Orders set successfully:', fetchedOrders.length);
    } catch (error) {
      console.error('❌ [PRODUCTION DEBUG] Error fetching Qargo orders:', error);
      console.error('❌ [PRODUCTION DEBUG] Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      // Set empty array on error so user knows fetch failed
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  // When mock orders are provided (screenshot mode), use them and skip API fetch
  useEffect(() => {
    if (mockOrders !== undefined) {
      setOrders(mockOrders);
      setLoadingOrders(false);
    }
  }, [mockOrders]);

  /**
   * Core logic for Palletways status + detail repair (tt_basic + getconsignment).
   * Returns { ok, rateLimited?, retryAfterMs? } to allow background enrich retry logic.
   */
  const runPalletwaysStatusRefresh = useCallback(async (order: EnrichableOrder, options?: { silent?: boolean }): Promise<EnrichRefreshResult> => {
    const trackingId = String(order.trackingId || '').trim();
    if (!trackingId) return { ok: false };

    let alreadyRefreshing = false;
    setStatusRefreshingIds((prev) => {
      if (prev.has(trackingId)) {
        alreadyRefreshing = true;
        return prev;
      }
      return new Set(prev).add(trackingId);
    });
    if (alreadyRefreshing) return { ok: false };

    if (!options?.silent) setStatusRefreshError(null);
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.fleetIntelligence.palletwaysConsignmentStatus(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trackingId,
          consignmentNo: order.name || undefined,
          currentStatus: order.status || undefined,
        }),
      });
      const body = await response.json().catch(() => ({}));
      
      // HTTP 429 rate limit
      if (response.status === 429) {
        const retryAfterMs = body?.retryAfterMs || 5000;
        return { ok: false, rateLimited: true, retryAfterMs };
      }

      if (!response.ok) {
        throw new Error(body?.error || `Status refresh failed (${response.status})`);
      }

      const nextStatus = String(body?.palletwaysStatus || '').trim();
      if (!nextStatus && !body?.detailRepaired) {
        throw new Error('Palletways returned no status or consignment detail');
      }

      const patchOrder = (row: Order): Order =>
        applyConsignmentStatusPatch(row, order, trackingId, body as Record<string, unknown>);

      setOrders((prev) => prev.map(patchOrder));
      setSelectedOrder((prev) => (prev ? patchOrder(prev) : prev));
      return { ok: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to refresh status';
      if (!options?.silent) setStatusRefreshError(message);
      console.error('[OrdersSection] Palletways status refresh failed:', err);
      return { ok: false };
    } finally {
      setStatusRefreshingIds((prev) => {
        const next = new Set(prev);
        next.delete(trackingId);
        return next;
      });
    }
  }, []);

  /**
   * User-triggered refresh (button click) — wraps runPalletwaysStatusRefresh.
   */
  const refreshPalletwaysStatus = useCallback(async (order: Order, e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await runPalletwaysStatusRefresh(order);
  }, [runPalletwaysStatusRefresh]);

  // Fetch orders on mount to load from cached export files immediately
  // This ensures orders display cached data on initial login, just like other sections
  // Also fetch when data prop becomes available (from parent's fetchOverviewData)
  useEffect(() => {
    // Only fetch if not using mock data and orders haven't been loaded yet
    if (mockOrders === undefined && orders.length === 0 && !loadingOrders) {
      console.log('[OrdersSection] Fetching orders from cached export files...', {
        hasData: !!data,
        ordersCount: orders.length
      });
      fetchOrders().catch(err => {
        console.error('[OrdersSection] Failed to fetch orders:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]); // Run on mount AND when data prop becomes available

  // Orders refresh is driven by parent "Refresh All" (onFetchData → mockOrders update).

  // Cheap Iceberg day rollup for volume chart — does not trigger vendor ingest
  useEffect(() => {
    let cancelled = false;
    const days =
      selectedPeriod === 'last_30_days'
        ? 30
        : selectedPeriod === 'last_180_days' || selectedPeriod === 'all-time'
          ? 90
          : 14;
    void authenticatedFetch(API_ENDPOINTS.fleetIntelligence.orders.activityByDay(days))
      .then(async (res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled || !json?.series || !Array.isArray(json.series)) return;
        const byDate: Record<string, { orders: number; completed: number; open: number }> = {};
        for (const row of json.series as Array<{
          date?: string;
          orders?: number;
          completed?: number;
          cancelled?: number;
          open?: number;
        }>) {
          const date = String(row.date ?? '');
          const orders = Number(row.orders) || 0;
          if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || orders <= 0) continue;
          const completed = Number(row.completed) || 0;
          const cancelled = Number(row.cancelled) || 0;
          const open = row.open != null
            ? Math.max(0, Number(row.open) || 0)
            : Math.max(0, orders - completed - cancelled);
          byDate[date] = { orders, completed, open };
        }
        if (Object.keys(byDate).length > 0) setOrdersActivityByDate(byDate);
      })
      .catch(() => {
        /* non-blocking — chart falls back to list bucketing */
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPeriod]);

  // Date + search filter only. Sort is applied separately so Order List header clicks
  // do not reshuffle Top Customers / locations / KPI aggregates above the table.
  const filteredOrders = useMemo(() => {
    // If no orders loaded, return empty array
    if (!orders || orders.length === 0) {
      return [];
    }
    
    let result = [...orders];
    
    // For "all-time", skip date filtering entirely - show ALL orders
    if (selectedPeriod !== 'all-time') {
      // Date filter based on selected period
      const { startDate, endDate } = getDateRange;
      
      // Filter orders by date range
      const ordersInRange: Order[] = [];
      const ordersWithoutDates: Order[] = [];
      const ordersOutsideRange: Order[] = [];
      
      result.forEach(order => {
        // Prefer primary date from CSV "date" column, then delivery date, then pickup date, then created date
        const dateStr = order.date || order.delivery?.date || order.pickup?.date || order.created?.date;
        
        if (!dateStr) {
          // Orders without dates: include them so user can see orders exist
          ordersWithoutDates.push(order);
          return;
        }
        
        // Parse date string (format: YYYY-MM-DD) - extract date part only
        const orderDateStr = dateStr.split('T')[0]; // Handle both YYYY-MM-DD and ISO strings
        if (!orderDateStr || orderDateStr.length !== 10) {
          ordersWithoutDates.push(order);
          return;
        }
        
        // Create UTC date for comparison (timezone-agnostic)
        const orderDate = new Date(orderDateStr + 'T00:00:00.000Z');
        
        // Check if date is valid
        if (isNaN(orderDate.getTime())) {
          ordersWithoutDates.push(order);
          return;
        }
        
        // Check if date is within range
        if (orderDate >= startDate && orderDate <= endDate) {
          ordersInRange.push(order);
        } else {
          ordersOutsideRange.push(order);
        }
      });
      
      // Priority: Show orders in date range first, then orders without dates (so user knows orders exist)
      // Only exclude orders that are clearly outside the date range
      if (ordersInRange.length > 0) {
        // We have orders in the date range - show only those
        result = ordersInRange;
      } else if (ordersWithoutDates.length > 0) {
        // No orders match the date range, but we have orders without dates
        // Show them so user knows orders exist (they just don't have dates)
        result = ordersWithoutDates;
      } else {
        // All orders have dates but none match the range - show empty
        result = [];
      }
    }
    
    // Debug logging
    if (result.length > 0 && result.length <= 5) {
      console.log('🔍 [Orders Debug] Sample orders:', result.slice(0, 3).map(o => ({
        id: o.id,
        primaryDate: o.date, // Primary date from CSV "date" column
        deliveryDate: o.delivery?.date,
        pickupDate: o.pickup?.date,
        createdDate: o.created?.date,
        hasDate: !!(o.date || o.delivery?.date || o.pickup?.date || o.created?.date)
      })));
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(order => 
        order.id.toLowerCase().includes(query) ||
        order.name.toLowerCase().includes(query) ||
        order.customerRef?.toLowerCase().includes(query) ||
        order.pickup?.address?.toLowerCase().includes(query) ||
        order.pickup?.postcode?.toLowerCase().includes(query) ||
        order.delivery?.address?.toLowerCase().includes(query) ||
        order.delivery?.postcode?.toLowerCase().includes(query)
      );
    }

    console.log(`📊 [Orders Debug] Showing ${result.length} orders from ${orders.length} total (period: ${selectedPeriod})`);
    return result;
  }, [orders, searchQuery, selectedPeriod, getDateRange]);

  // Table-only sort — must not feed charts / Top Customers / KPI cards
  const orderDateValue = (o: Order) =>
    (o.date || o.delivery?.date || o.pickup?.date || o.created?.date || '').trim().split('T')[0];

  const sortedOrders = useMemo(() => {
    const result = [...filteredOrders];
    result.sort((a, b) => {
      const strVal = (s: string | undefined) => (s || '').trim();

      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = strVal(a.name).localeCompare(strVal(b.name), undefined, { numeric: true });
          break;
        case 'status':
          cmp = strVal(a.status).localeCompare(strVal(b.status), undefined, { numeric: true });
          break;
        case 'type':
          cmp = strVal(a.type).localeCompare(strVal(b.type), undefined, { numeric: true });
          break;
        case 'date': {
          const dateA = orderDateValue(a);
          const dateB = orderDateValue(b);
          if (!dateA && !dateB) cmp = 0;
          else if (!dateA) cmp = 1;
          else if (!dateB) cmp = -1;
          else cmp = dateA.localeCompare(dateB);
          break;
        }
        case 'customerRef':
          cmp = strVal(a.customerRef).localeCompare(strVal(b.customerRef), undefined, { numeric: true });
          break;
        case 'pickup':
          cmp = strVal(a.pickup?.address || a.pickup?.name).localeCompare(strVal(b.pickup?.address || b.pickup?.name), undefined, { numeric: true });
          break;
        case 'delivery':
          cmp = strVal(a.delivery?.address || a.delivery?.name).localeCompare(strVal(b.delivery?.address || b.delivery?.name), undefined, { numeric: true });
          break;
        case 'weightKg':
          cmp = (a.weightKg ?? 0) - (b.weightKg ?? 0);
          break;
        default: // 'id' — sort by displayed Order ID (con_no / name), not internal entity id
          cmp = strVal(a.name || a.id).localeCompare(strVal(b.name || b.id), undefined, { numeric: true });
      }
      if (cmp === 0) {
        // Stable tie-break so sort clicks don't reshuffle equal rows
        cmp = strVal(a.id).localeCompare(strVal(b.id));
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [filteredOrders, sortField, sortDirection]);

  // Analytics / KPIs use filtered (unsorted) orders
  const processedOrders = filteredOrders;

  // Pagination
  const totalPages = Math.ceil(sortedOrders.length / rowsPerPage);
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return sortedOrders.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedOrders, currentPage, rowsPerPage]);

  // Reset to page 1 when filters change (including date range)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortField, sortDirection, selectedPeriod, customStartDate, customEndDate]);

  // Demo exports are snapshots; quick ranges (today/last-week) may hide all loaded orders.
  // Auto-switch once so users immediately see loaded demo orders in the list.
  useEffect(() => {
    if (
      isDemoUserForOrders &&
      !autoSwitchedToAllTime &&
      orders.length > 0 &&
      selectedPeriod !== 'all-time' &&
      processedOrders.length === 0
    ) {
      setSelectedPeriod('last-week');
      setAutoSwitchedToAllTime(true);
    }
  }, [isDemoUserForOrders, autoSwitchedToAllTime, orders.length, selectedPeriod, processedOrders.length]);

  // Build orders_by_date and status counts FROM THE ORDER LIST when we have orders (single source of truth)
  const ordersByDateFromList = useMemo(() => {
    if (!orders || orders.length === 0) return {} as Record<string, number>;
    const byDate: Record<string, number> = {};
    orders.forEach(order => {
      const dateStr = (order.date || order.delivery?.date || order.pickup?.date || order.created?.date || '')
        .toString().trim().split('T')[0];
      if (dateStr && dateStr.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        byDate[dateStr] = (byDate[dateStr] || 0) + 1;
      }
    });
    return byDate;
  }, [orders]);

  const getOrderStatus = (order: Order) =>
    (order.status || order._raw?.status || 'unknown').toString().trim();
  const statusCountsFromFullList = useMemo(() => {
    if (!orders || orders.length === 0) return { completed: 0, inProgress: 0, pending: 0, cancelled: 0 };
    let completed = 0, inProgress = 0, pending = 0, cancelled = 0;
    orders.forEach(order => {
      switch (classifyOrderStatus(getOrderStatus(order))) {
        case 'completed': completed++; break;
        case 'in_progress': inProgress++; break;
        case 'cancelled': cancelled++; break;
        default: pending++; break;
      }
    });
    return { completed, inProgress, pending, cancelled };
  }, [orders]);

  // Effective data: prefer order list when available so boxes are never empty due to missing analytics
  const effectiveData = useMemo((): QargoData | null => {
    const activityTotals = ordersActivityByDate
      ? Object.fromEntries(
        Object.entries(ordersActivityByDate).map(([day, row]) => [day, row.orders]),
      )
      : null;
    const chartByDate =
      activityTotals && Object.keys(activityTotals).length > 0
        ? activityTotals
        : Object.keys(ordersByDateFromList).length > 0
          ? ordersByDateFromList
          : undefined;

    if (orders && orders.length > 0) {
      return {
        order_count: orders.length,
        company_count: data?.company_count ?? 0,
        resource_count: data?.resource_count ?? 0,
        completed_orders: statusCountsFromFullList.completed,
        in_progress_orders: statusCountsFromFullList.inProgress,
        pending_orders: statusCountsFromFullList.pending,
        cancelled_orders: statusCountsFromFullList.cancelled,
        delivery_performance: orders.length > 0
          ? Math.round((statusCountsFromFullList.completed / orders.length) * 100)
          : 0,
        orders_by_date: chartByDate,
        orders_by_status: data?.orders_by_status,
      };
    }
    if (chartByDate) {
      return {
        order_count: data?.order_count ?? 0,
        company_count: data?.company_count ?? 0,
        resource_count: data?.resource_count ?? 0,
        completed_orders: data?.completed_orders ?? 0,
        in_progress_orders: data?.in_progress_orders ?? 0,
        pending_orders: data?.pending_orders ?? 0,
        cancelled_orders: data?.cancelled_orders ?? 0,
        delivery_performance: data?.delivery_performance ?? 0,
        orders_by_date: chartByDate,
        orders_by_status: data?.orders_by_status,
      };
    }
    return data;
  }, [orders, data, ordersByDateFromList, ordersActivityByDate, statusCountsFromFullList]);

  // Filter data by date range (uses effectiveData so list-derived metrics are used when available)
  const filteredData = useMemo(() => {
    if (!effectiveData) return null;

    const { startDate, endDate } = getDateRange;
    const baseOrdersByDate = effectiveData.orders_by_date && Object.keys(effectiveData.orders_by_date).length > 0
      ? effectiveData.orders_by_date
      : ordersByDateFromList;

    const filtered: QargoData = { ...effectiveData };

    if (!baseOrdersByDate || Object.keys(baseOrdersByDate).length === 0) {
      filtered.orders_by_date = {};
      filtered.order_count = processedOrders.length;
      if (processedOrders.length > 0) {
        let c = 0, i = 0, p = 0, x = 0;
        processedOrders.forEach(o => {
          switch (classifyOrderStatus(getOrderStatus(o))) {
            case 'completed': c++; break;
            case 'in_progress': i++; break;
            case 'cancelled': x++; break;
            default: p++; break;
          }
        });
        filtered.completed_orders = c;
        filtered.in_progress_orders = i;
        filtered.pending_orders = p;
        filtered.cancelled_orders = x;
      } else {
        filtered.completed_orders = 0;
        filtered.in_progress_orders = 0;
        filtered.pending_orders = 0;
        filtered.cancelled_orders = 0;
      }
      return filtered;
    }

    const filteredByDate: Record<string, number> = {};
    let filteredOrderCount = 0;
    Object.entries(baseOrdersByDate).forEach(([date, count]) => {
      const dateStr = date.split('T')[0];
      if (!dateStr || dateStr.length !== 10) return;
      const orderDate = new Date(dateStr + 'T00:00:00.000Z');
      if (orderDate >= startDate && orderDate <= endDate) {
        filteredByDate[date] = count;
        filteredOrderCount += count;
      }
    });

    filtered.orders_by_date = filteredByDate;
    filtered.order_count = filteredOrderCount;

    if (filteredOrderCount === 0) {
      filtered.completed_orders = 0;
      filtered.in_progress_orders = 0;
      filtered.pending_orders = 0;
      filtered.cancelled_orders = 0;
    } else if (processedOrders.length > 0) {
      let c = 0, i = 0, p = 0, x = 0;
      processedOrders.forEach(order => {
        switch (classifyOrderStatus(getOrderStatus(order))) {
          case 'completed': c++; break;
          case 'in_progress': i++; break;
          case 'cancelled': x++; break;
          default: p++; break;
        }
      });
      filtered.completed_orders = c;
      filtered.in_progress_orders = i;
      filtered.pending_orders = p;
      filtered.cancelled_orders = x;
    }

    return filtered;
  }, [effectiveData, getDateRange, processedOrders, ordersByDateFromList]);

  // Calculate status counts from processedOrders if available, otherwise use analytics data
  const statusCountsFromOrders: Record<string, number> = {};
  if (processedOrders.length > 0) {
    processedOrders.forEach(order => {
      const bucket = classifyOrderStatus(getOrderStatus(order));
      const normalizedStatus =
        bucket === 'completed' ? 'COMPLETED' :
        bucket === 'in_progress' ? 'IN_PROGRESS' :
        bucket === 'cancelled' ? 'CANCELLED' :
        'PENDING';
      statusCountsFromOrders[normalizedStatus] = (statusCountsFromOrders[normalizedStatus] || 0) + 1;
    });
  }
  
  // Use calculated counts if orders are available, otherwise fall back to analytics data
  const completedOrders = processedOrders.length > 0 
    ? (statusCountsFromOrders['COMPLETED'] || 0)
    : (filteredData?.completed_orders ?? data?.completed_orders ?? 0);
  const inProgressOrders = processedOrders.length > 0
    ? (statusCountsFromOrders['IN_PROGRESS'] || 0)
    : (filteredData?.in_progress_orders ?? data?.in_progress_orders ?? 0);
  const pendingOrders = processedOrders.length > 0
    ? (statusCountsFromOrders['PENDING'] || 0)
    : (filteredData?.pending_orders ?? data?.pending_orders ?? 0);
  const cancelledOrders = processedOrders.length > 0
    ? (statusCountsFromOrders['CANCELLED'] || 0)
    : (filteredData?.cancelled_orders ?? data?.cancelled_orders ?? 0);

  // Period total for percentages: use sum of status counts so % always matches displayed counts
  const periodTotalForPct = completedOrders + inProgressOrders + pendingOrders + cancelledOrders;
  const totalOrders = periodTotalForPct > 0 ? periodTotalForPct : (processedOrders.length > 0 ? processedOrders.length : (filteredData?.order_count ?? 0));

  // Core metrics (always use period total so percentages match the four status cards)
  const cancellationRate = totalOrders > 0 ? (cancelledOrders / totalOrders) * 100 : 0;

  // Additional Analytics - Use processedOrders (filtered by date) for all calculations
  const avgOrderWeight = useMemo(() => {
    const ordersWithWeight = processedOrders.filter(o => o.weightKg && o.weightKg > 0);
    if (ordersWithWeight.length === 0) return 0;
    return ordersWithWeight.reduce((sum, o) => sum + (o.weightKg || 0), 0) / ordersWithWeight.length;
  }, [processedOrders]);

  const topCustomers = useMemo(() => {
    const customerCounts: Record<string, number> = {};
    processedOrders.forEach(order => {
      const customer = order.customerRef || 'Unknown';
      customerCounts[customer] = (customerCounts[customer] || 0) + 1;
    });
    return Object.entries(customerCounts)
      .sort(([ca, a], [cb, b]) => b - a || ca.localeCompare(cb))
      .slice(0, 5)
      .map(([customer, count]) => ({ customer, count }));
  }, [processedOrders]);

  const totalWeight = useMemo(() => {
    return processedOrders.reduce((sum, o) => sum + (o.weightKg || 0), 0);
  }, [processedOrders]);

  // Order volume bars — same pattern as Maintenance fault chart:
  // single-day filters keep a 7-day context and highlight the filtered day.
  const isSingleDayPeriod =
    selectedPeriod === 'today'
    || selectedPeriod === 'yesterday'
    || (
      getDateRange.endDate.getTime() - getDateRange.startDate.getTime() < 26 * 60 * 60 * 1000
    );

  const { days: orderVolumeChartDays, maxCount: maxOrderVolumeDay, todayKey: ordersEnteredTodayKey } = useMemo(() => {
    const byDay = new Map<string, { orders: number; completed: number; open: number }>();

    if (ordersActivityByDate && Object.keys(ordersActivityByDate).length > 0) {
      for (const [day, row] of Object.entries(ordersActivityByDate)) {
        const key = day.includes('T') ? day.split('T')[0] : day;
        byDay.set(key, {
          orders: row.orders,
          completed: row.completed,
          open: row.open,
        });
      }
    } else {
      const fallback =
        (effectiveData?.orders_by_date && Object.keys(effectiveData.orders_by_date).length > 0
          ? effectiveData.orders_by_date
          : null)
        || ordersByDateFromList;
      for (const [day, count] of Object.entries(fallback || {})) {
        const key = day.includes('T') ? day.split('T')[0] : day;
        const orders = Number(count) || 0;
        byDay.set(key, { orders, completed: 0, open: orders });
      }
    }

    const selectedStartKey = getDateRange.startDate.toISOString().split('T')[0];
    const selectedEndKey = getDateRange.endDate.toISOString().split('T')[0];
    const todayKey = londonDateString();

    let chartStartKey: string;
    let chartEndKey: string;
    let maxBars = 14;

    if (isSingleDayPeriod) {
      chartEndKey = todayKey;
      chartStartKey = shiftYmd(todayKey, -6);
      maxBars = 7;
    } else {
      chartEndKey = selectedEndKey;
      const rangeDays = Math.max(
        1,
        Math.round(
          (getDateRange.endDate.getTime() - getDateRange.startDate.getTime()) / (24 * 60 * 60 * 1000),
        ) + 1,
      );
      const span = Math.min(14, rangeDays) - 1;
      const windowStart = shiftYmd(chartEndKey, -span);
      chartStartKey = windowStart < selectedStartKey ? selectedStartKey : windowStart;
      maxBars = 14;
    }

    const days: Array<{
      day: string;
      label: string;
      count: number;
      completed: number;
      open: number;
      periodHighlighted: boolean;
    }> = [];
    let maxCount = 1;
    for (let i = 0; i < maxBars; i++) {
      const key = shiftYmd(chartStartKey, i);
      if (key > chartEndKey) break;
      const weekday = ymdWeekday(key);
      const row = byDay.get(key) ?? { orders: 0, completed: 0, open: 0 };
      if (row.orders > maxCount) maxCount = row.orders;
      days.push({
        day: key,
        label: weekday.toUpperCase().slice(0, 3),
        count: row.orders,
        completed: row.completed,
        open: row.open,
        periodHighlighted: isSingleDayPeriod && key >= selectedStartKey && key <= selectedEndKey,
      });
    }
    return { days, maxCount, todayKey };
  }, [
    ordersActivityByDate,
    effectiveData?.orders_by_date,
    ordersByDateFromList,
    getDateRange,
    isSingleDayPeriod,
  ]);

  // After all hooks — early return before this crashed React when data loaded
  // (hooks after the old guard ran only once data became non-null).
  if (!data && mockOrders === undefined && orders.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Order Flow Intelligence</h3>
        <p className="text-muted-foreground mb-2">Advanced order analytics with interactive filtering</p>
        <p className="text-muted-foreground text-sm mb-6">
          Data loads from {orderSourceLabel || 'your configured order systems'}. Click below to fetch the overview and order list.
        </p>
        <button
          onClick={onFetchData}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-6 py-3 bg-chart-5 hover:bg-chart-5 disabled:bg-chart-5/50 text-white rounded-lg transition-colors"
        >
          <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          {isLoading ? 'Loading...' : 'Load Order Data'}
        </button>
      </div>
    );
  }

  // Trend analysis
  const dateEntries = filteredData?.orders_by_date ? Object.entries(filteredData.orders_by_date).sort(([a], [b]) => a.localeCompare(b)) : [];
  const recentDays = dateEntries.slice(-7);
  const previousDays = dateEntries.slice(-14, -7);
  const recentAvg = recentDays.length > 0 ? recentDays.reduce((sum, [, count]) => sum + count, 0) / recentDays.length : 0;
  const previousAvg = previousDays.length > 0 ? previousDays.reduce((sum, [, count]) => sum + count, 0) / previousDays.length : 0;
  const trendChange = previousAvg > 0 ? ((recentAvg - previousAvg) / previousAvg) * 100 : 0;

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };

  const periodBar = (
    <ControlCentrePeriodPanel
      value={selectedPeriod}
      onChange={handlePeriodChange}
      ariaLabel="Order time period"
    />
  );

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-2xl font-bold text-white flex items-center gap-3">
              <Package className="w-7 h-7 text-chart-5" />
              Order Flow Intelligence
            </h3>
            <p className="text-muted-foreground mt-1">{ordersAnalyticsSubtitle(orderSourceIds)}</p>
        </div>
          <ControlCentreSourceBadge label={orderSourceLabel} />
      </div>

        {/* Time period — with Palletways, chips sit on the Refresh figures row */}
        {(!ordersDailyEnabled || (exportDateRange?.start && exportDateRange?.end)) && (
        <div className="flex flex-col gap-1">
          {!ordersDailyEnabled && periodBar}
          {exportDateRange?.start && exportDateRange?.end && (() => {
            const todayUTC = new Date().toISOString().split('T')[0];
            const exportEndDate = exportDateRange.end;
            const isOutdated = exportEndDate < todayUTC;
            const daysOld = isOutdated ? Math.floor((new Date(todayUTC).getTime() - new Date(exportEndDate).getTime()) / (1000 * 60 * 60 * 24)) : 0;
            
            return (
              <div className={`rounded-lg border px-4 py-2 text-sm flex flex-wrap items-center gap-x-2 gap-y-1 ${
                isOutdated 
                  ? 'border-warning/40 bg-warning/10 text-warning'
                  : 'border-ring/40 bg-ring/10 text-ring'
              }`}>
                <Calendar className={`w-4 h-4 shrink-0 ${isOutdated ? 'text-warning' : 'text-ring'}`} />
                <span>
                  <strong>Loaded export covers:</strong>{' '}
                  {exportDateRange.start} → {exportDateRange.end}
                  {isOutdated && <strong className="text-warning"> ({daysOld} {daysOld === 1 ? 'day' : 'days'} old)</strong>}
                </span>
                {isOutdated && (
                  <span className="text-warning font-medium">
                    ⚠️ Data is outdated. Use the "Refresh All" button at the top to get today's orders (UTC: {todayUTC}).
                  </span>
                )}
              </div>
            );
          })()}
        </div>
        )}

        {/* Hint when selected period has no orders but data exists for other ranges */}
        {!ordersDailyEnabled && selectedPeriod !== 'all-time' && (filteredData?.order_count === 0 || totalOrders === 0) && (orders.length > 0 || (data?.order_count ?? 0) > 0) && (
          <div className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-2 text-sm text-warning">
            No orders in this period. Try <button type="button" className="font-medium underline hover:text-warning" onClick={() => handlePeriodChange('last-week')}>Last 7 Days</button> or another range.
          </div>
        )}
      </div>

      {/* Key Performance Metrics */}
      {ordersDailyEnabled ? (
        <div ref={kpiGridRef}>
          <OrdersDailyKpiSection
            startDate={getDateRange.startDate.toISOString().split('T')[0]}
            endDate={getDateRange.endDate.toISOString().split('T')[0]}
            highlightKPI={highlightKPI}
            onHighlightConsumed={onHighlightConsumed}
            periodBar={periodBar}
            demo={demoMode}
          />
        </div>
      ) : (
      <div ref={kpiGridRef} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ControlCentreKPICard
          id="kpi-collections"
          title="Collections"
          value="TBC"
          subtitle="Definition pending — see orders KPI notes"
          icon={Truck}
          variant="primary"
          colorScheme="blue"
          valueHoverScale={1.3}
        />

        <ControlCentreKPICard
          id="kpi-deliveries"
          title="Deliveries"
          value="TBC"
          subtitle="Definition pending — see orders KPI notes"
          icon={Package}
          variant="primary"
          colorScheme="cyan"
          valueHoverScale={1.3}
        />
      </div>
      )}

      {!ordersDailyEnabled && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Average Weight */}
        <div
          id="kpi-avg-weight"
          onClick={() => setKpiModal({ open: true, kpi: 'weight', title: 'Orders by Weight', subtitle: `Average: ${avgOrderWeight.toFixed(0)} kg | Total: ${totalWeight.toLocaleString()} kg` })}
          className="cursor-pointer bg-gradient-to-br from-chart-10/15 to-chart-10/5 rounded-lg p-5 border border-chart-10/30 hover:border-chart-10/50 transition-all hover:shadow-lg hover:shadow-chart-10/10 hover:scale-102"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-chart-10/30 p-2.5 rounded-lg border border-chart-10/30">
              <Weight className="w-5 h-5 text-chart-10" />
            </div>
            <div>
              <div className="text-2xl font-bold text-chart-10">
                {avgOrderWeight.toFixed(0)} kg
              </div>
              <div className="text-xs text-chart-10/80 font-medium">Avg Weight</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground bg-background/30 rounded px-2 py-1 flex items-center justify-between">
            <span>{totalWeight.toLocaleString()} kg total volume</span>
            <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">Click to view</span>
          </div>
        </div>

        {/* Unique Customers */}
        <div
          id="kpi-customers"
          onClick={() => setKpiModal({ open: true, kpi: 'customers', title: 'Customer Orders', subtitle: `${topCustomers.length} unique customers` })}
          className="cursor-pointer bg-gradient-to-br from-chart-5/15 to-chart-5/5 rounded-lg p-5 border border-chart-5/30 hover:border-chart-5/50 transition-all hover:shadow-lg hover:shadow-chart-5/10 hover:scale-102"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-chart-5/30 p-2.5 rounded-lg border border-chart-5/30">
              <User className="w-5 h-5 text-chart-5" />
            </div>
            <div>
              <div className="text-2xl font-bold text-chart-5">
                {topCustomers.length > 0 ? topCustomers.length : 0}
              </div>
              <div className="text-xs text-chart-5/80 font-medium">Customers</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground bg-background/30 rounded px-2 py-1 truncate">
            Top: {topCustomers[0]?.customer || 'N/A'}
          </div>
        </div>

        {/* Cancellation Rate */}
        <div
          id="kpi-cancelled"
          onClick={() => setKpiModal({ open: true, kpi: 'cancelled', title: 'Cancelled Orders', subtitle: `${cancellationRate.toFixed(1)}% cancellation rate`, filterFn: (o: any) => /cancel/i.test(o.status || '') })}
          className="cursor-pointer bg-gradient-to-br from-destructive/15 to-destructive/5 rounded-lg p-5 border border-destructive/30 hover:border-destructive/50 transition-all hover:shadow-lg hover:shadow-destructive/10 hover:scale-102"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-destructive/30 p-2.5 rounded-lg border border-destructive/30">
              <XCircle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <div className="text-2xl font-bold text-destructive">
                {cancellationRate.toFixed(1)}%
              </div>
              <div className="text-xs text-destructive/80 font-medium">Cancelled</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground bg-background/30 rounded px-2 py-1">
            {cancelledOrders.toLocaleString()} orders cancelled
          </div>
        </div>
      </div>
      )}

      {/* Charts.
          Deliberately NOT shown on the operational dashboard (ordersDailyEnabled).
          "Orders entered" is keyed on the date a record entered the system, which
          is precisely the date that does not describe today's workload — showing it
          next to the operational figures teaches the wrong mental model. The legacy
          Qargo view keeps it until that path gets its own operational screen. */}
      {!ordersDailyEnabled && (
      <div className="space-y-6">
        {/* Orders Entered — by date entered; stacked completed vs open */}
        <OrdersEnteredChart
          days={orderVolumeChartDays}
          maxCount={maxOrderVolumeDay}
          todayKey={ordersEnteredTodayKey}
          isSingleDayPeriod={isSingleDayPeriod}
          selectedPeriod={selectedPeriod}
          trendChange={trendChange}
        />
        </div>
      )}

      {/* Orders List — Qargo and other sources. Palletways uses the stored orders_daily table above. */}
      {!ordersDailyEnabled && (
      <div className="bg-card/50 rounded-lg p-5 border border-border">
        <div className="flex flex-col gap-4 mb-4">
          {/* Header */}
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h4 className="text-lg font-semibold text-white">Orders List</h4>
              <p className="text-sm text-muted-foreground mt-1">
                {processedOrders.length} {processedOrders.length === 1 ? 'order' : 'orders'}
                {searchQuery && ` matching "${searchQuery}"`}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {loadingOrders && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Loading...</span>
                </div>
              )}
            </div>
          </div>

          {/* Search and Controls */}
          {orders.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="flex-1 min-w-63">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search orders by ID, name, customer, or address..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-secondary border border-border rounded-lg text-sm text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-chart-5"
                  />
                </div>
              </div>

              {/* Rows per page */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Show:</label>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-2 bg-secondary border border-border rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-chart-5"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              {/* Export */}
              <button
                onClick={() => {
                  // Export visible orders as CSV
                  const headers = hideNameColumn
                    ? ['Order ID', 'Date', 'Status', 'Type', 'Customer', 'Origin', 'Destination', 'Weight (kg)', 'Lifts']
                    : ['Order ID', 'Name', 'Date', 'Status', 'Customer', 'Pickup', 'Delivery', 'Weight (kg)'];
                  const csvContent = [
                    headers,
                    ...sortedOrders.map(order => {
                      const origin = order.pickup?.address || [order.pickup?.name, order.pickup?.postcode].filter(Boolean).join(', ') || '';
                      const destination = order.delivery?.address
                        || [order.delivery?.name, order.delivery?.postcode].filter(Boolean).join(', ')
                        || '';
                      const orderId = hideNameColumn || order.source === 'palletways'
                        ? (order.name || order.id)
                        : order.id;
                      const dateVal = order.date || order.pickup?.date || order.delivery?.date || '';
                      const row = hideNameColumn
                        ? [
                            orderId,
                            dateVal,
                            order.status || '',
                            order.type || '',
                            order.customerRef || '',
                            origin,
                            destination,
                            order.weightKg || '',
                            order.lifts ?? '',
                          ]
                        : [
                            orderId,
                            order.name,
                            dateVal,
                            order.status || '',
                            order.customerRef || '',
                            origin,
                            destination,
                            order.weightKg || '',
                          ];
                      return row;
                    }),
                  ].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
                  
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `orders-export-${new Date().toISOString().split('T')[0]}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
                className="inline-flex items-center gap-2 px-3 py-2 bg-secondary hover:bg-accent text-white text-sm rounded-lg transition-colors border border-border"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          )}
        </div>
        {loadingOrders ? (
          <div className="text-center py-8">
            <RefreshCw className="w-8 h-8 text-muted-foreground mx-auto mb-2 animate-spin" />
            <p className="text-sm text-muted-foreground">Loading orders from API...</p>
          </div>
        ) : !orders || orders.length === 0 ? (
          <div className="text-center py-8">
            <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-2">No orders loaded for this period</p>
            <p className="text-xs text-muted-foreground">
              Orders load from {orderSourceLabel || 'your configured order systems'}.
              {totalOrders > 0 && ` Analytics shows ${totalOrders.toLocaleString()} orders in this period.`}
              {data?.order_count && data.order_count > 0 && ` Total orders available: ${data.order_count.toLocaleString()}.`}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {orders.length === 0 
                ? 'Use the "Refresh All" button at the top of the Control Centre to trigger a new export, or check the browser console for API errors.'
                : 'Try Last 7 Days, or check that orders have dates in the export.'}
            </p>
          </div>
        ) : processedOrders.length === 0 ? (
          <div className="text-center py-8">
            <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-2">No orders found</p>
            <p className="text-xs text-muted-foreground mb-3">
              {searchQuery 
                ? `No orders match "${searchQuery}"` 
                : selectedPeriod === 'all-time'
                  ? orders.length === 0
                    ? 'No orders found in export files.'
                    : `No orders found in export. Total loaded: ${orders.length} orders.`
                  : orders.length > 0
                    ? `No orders match the selected date range. ${orders.length} orders are loaded but don't have dates in this window. Try Last 7 Days.`
                    : 'No orders available for this date range. Use "Refresh All" at the top of Control Centre to pull the latest orders.'}
            </p>
            {orders.length > 0 && selectedPeriod !== 'all-time' && (
              <p className="text-xs text-muted-foreground mt-2">
                Tip: Try Last 7 Days to see more of the {orders.length} loaded orders, or check that dates are populated in the export.
              </p>
            )}
            {orders.length === 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                💡 Tip: Use "Refresh All" to sync from {orderSourceLabel || 'your order systems'}. This may take 10-30 seconds.
              </p>
            )}
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            {statusRefreshError && (
              <div className="mb-3 flex items-start justify-between gap-3 rounded-lg border border-destructive/40 bg-destructive/20 px-3 py-2 text-sm text-destructive">
                <span>Refresh failed: {statusRefreshError}</span>
                <button
                  type="button"
                  onClick={() => setStatusRefreshError(null)}
                  className="shrink-0 text-destructive hover:text-white"
                  aria-label="Dismiss status refresh error"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-white/10">
                  {(() => {
                    const sortHeaderBtn = (field: SortField, label: string) => {
                      const isActive = sortField === field;
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSort(field);
                          }}
                          title={`Sort by ${label}${isActive ? ` (${sortDirection})` : ''}`}
                          className={`inline-flex items-center gap-1 text-sm font-semibold transition-colors focus:outline-none ${
                            isActive ? 'text-chart-5' : 'text-foreground hover:text-white'
                          }`}
                        >
                          <span>{label}</span>
                          {isActive && (
                            sortDirection === 'asc'
                              ? <ChevronUp className="w-4 h-4 text-chart-5 shrink-0" aria-hidden />
                              : <ChevronDown className="w-4 h-4 text-chart-5 shrink-0" aria-hidden />
                          )}
                        </button>
                      );
                    };

                    // Palletways Control Centre: Customer (col_company) + Origin/Destination places
                    const columns: Array<{ field: SortField; label: string; align: string }> = [
                      { field: 'id', label: 'Order ID', align: 'text-left' },
                      ...(!hideNameColumn ? [{ field: 'name' as SortField, label: 'Name', align: 'text-left' }] : []),
                      { field: 'date', label: 'Date', align: 'text-left' },
                      { field: 'status', label: 'Status', align: 'text-left' },
                      ...(hideNameColumn ? [{ field: 'type' as SortField, label: 'Type', align: 'text-left' }] : []),
                      { field: 'customerRef', label: 'Customer', align: 'text-left' },
                      { field: 'pickup', label: hideNameColumn ? 'Origin' : 'Pickup', align: 'text-left' },
                      { field: 'delivery', label: hideNameColumn ? 'Destination' : 'Delivery', align: 'text-left' },
                      { field: 'weightKg', label: 'Weight', align: 'text-right' },
                    ];

                    return columns.map(({ field, label, align }) => (
                      <th key={field} className={`${align} py-3 px-4`}>
                        {sortHeaderBtn(field, label)}
                      </th>
                    ));
                  })()}
                </tr>
              </thead>
              <tbody>
                  {paginatedOrders.map((order) => {
                  const statusColor = orderStatusTextClass(order.status);
                  const isRefreshing = order.trackingId ? statusRefreshingIds.has(order.trackingId) : false;
                  const customerLabel = isRefreshing && !order.customerRef ? 'Loading…' : (order.customerRef || '—');
                  const originLabel = isRefreshing && placeNameForTable(order.pickup) === '—' ? 'Loading…' : placeNameForTable(order.pickup);
                  const destinationLabel = isRefreshing && placeNameForTable(order.delivery) === '—' ? 'Loading…' : placeNameForTable(order.delivery);
                  
                  const orderIdFull = hideNameColumn || order.source === 'palletways'
                    ? (order.name || order.id)
                    : order.id;

                  return (
                    <tr
                      key={order.id}
                      onClick={() => handleOrderClick(order)}
                      className="border-b border-border/50 hover:bg-chart-5/20 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-4 text-sm text-chart-5 font-mono">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {order.source === 'palletways' && order.trackingId ? (
                            <button
                              type="button"
                              title="Refresh live status and fill missing customer / locations"
                              aria-label={`Refresh status and details for ${order.name || order.id}`}
                              disabled={statusRefreshingIds.has(order.trackingId)}
                              onClick={(e) => refreshPalletwaysStatus(order, e)}
                              className="shrink-0 p-1 rounded text-muted-foreground hover:text-chart-5 hover:bg-chart-5/40 disabled:opacity-50 disabled:cursor-wait transition-colors"
                            >
                              <RefreshCw
                                className={`w-3.5 h-3.5 ${statusRefreshingIds.has(order.trackingId) ? 'animate-spin' : ''}`}
                              />
                            </button>
                          ) : null}
                          <span className="truncate max-w-36" title={orderIdFull}>
                            {truncateOrderId(orderIdFull)}
                          </span>
                        </div>
                      </td>
                      {!hideNameColumn && (
                        <td className="py-3 px-4 text-sm text-white font-medium">{order.name}</td>
                      )}
                      <td className="py-3 px-4 text-sm text-foreground">
                        {order.date || order.delivery?.date || order.pickup?.date || order.created?.date || '—'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${statusColor} bg-secondary/50`}>
                          {order.status || 'Unknown'}
                        </span>
                      </td>
                      {hideNameColumn && (
                        <td className="py-3 px-4 text-sm text-foreground">{order.type || '—'}</td>
                      )}
                      <td className="py-3 px-4 text-sm text-foreground max-w-50 truncate" title={customerLabel !== '—' ? customerLabel : undefined}>
                        {customerLabel}
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground max-w-50 truncate" title={originLabel !== '—' ? originLabel : undefined}>
                        {originLabel}
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground max-w-50 truncate" title={destinationLabel !== '—' ? destinationLabel : undefined}>
                        {destinationLabel}
                      </td>
                      <td className="py-3 px-4 text-sm text-foreground text-right">
                        {order.weightKg ? `${order.weightKg.toFixed(0)} kg` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

            {/* Pagination Controls */}
            {processedOrders.length > rowsPerPage && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <div className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, processedOrders.length)} of {processedOrders.length} orders
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 bg-secondary hover:bg-accent disabled:bg-card disabled:text-muted-foreground text-white rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="text-sm text-foreground">
                    Page {currentPage} of {totalPages}
                  </div>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 bg-secondary hover:bg-accent disabled:bg-card disabled:text-muted-foreground text-white rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
          </div>
            )}
          </>
        )}
      </div>
      )}

      {/* Order Detail Modal */}
      {showOrderDetail && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowOrderDetail(false)}>
          <div className="bg-popover rounded-lg border border-border max-w-4xl w-full max-h-screen-90 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Package className="w-6 h-6 text-chart-5" />
                  Order Details
                </h3>
                <p className="text-sm text-muted-foreground mt-1">{selectedOrder.id}</p>
              </div>
              <button
                onClick={() => setShowOrderDetail(false)}
                className="p-2 hover:bg-secondary rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {(() => {
                const originAddress = distinctPlaceAddress(selectedOrder.pickup);
                const destinationAddress = distinctPlaceAddress(selectedOrder.delivery);
                return (
                  <>
              {/* Summary: Name, Status, Type, Created on one row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-secondary/50 rounded-lg p-4 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-ring shrink-0" />
                    <span className="text-sm text-muted-foreground">Order Name</span>
                  </div>
                  <p className="text-white font-semibold truncate" title={selectedOrder.name || selectedOrder.id}>
                    {selectedOrder.name || selectedOrder.id}
                  </p>
                </div>

                <div className="bg-secondary/50 rounded-lg p-4 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className={`w-4 h-4 shrink-0 ${orderStatusTextClass(selectedOrder.status)}`} />
                    <span className="text-sm text-muted-foreground">Status</span>
                  </div>
                  <p
                    className={`font-semibold truncate ${orderStatusTextClass(selectedOrder.status)}`}
                    title={selectedOrder.status || 'Unknown'}
                  >
                    {selectedOrder.status || 'Unknown'}
                  </p>
                </div>

                <div className="bg-secondary/50 rounded-lg p-4 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-4 h-4 text-chart-10 shrink-0" />
                    <span className="text-sm text-muted-foreground">Type</span>
                  </div>
                  <p className="text-white font-semibold truncate">{selectedOrder.type || '—'}</p>
                </div>

                <div className="bg-secondary/50 rounded-lg p-4 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-chart-5 shrink-0" />
                    <span className="text-sm text-muted-foreground">Created</span>
                  </div>
                  <p className="text-white font-semibold truncate">
                    {selectedOrder.created?.date
                      ? `${selectedOrder.created.date}${selectedOrder.created.time ? ` at ${selectedOrder.created.time}` : ''}`
                      : '—'}
                  </p>
                </div>
              </div>

              {/* Customer + locations + goods on one row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-secondary/50 rounded-lg p-4 min-w-0">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-5 h-5 text-warning shrink-0" />
                    <h4 className="text-lg font-semibold text-white">
                      {hideNameColumn ? 'Customer' : 'Customer Ref'}
                    </h4>
                  </div>
                  <p className="text-white font-semibold break-words">
                    {selectedOrder.customerRef || '—'}
                  </p>
                </div>

                <div className="bg-secondary/50 rounded-lg p-4 min-w-0">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-ring shrink-0" />
                    <h4 className="text-lg font-semibold text-white">
                      {hideNameColumn ? 'Origin' : 'Pickup Location'}
                    </h4>
                  </div>
                  {selectedOrder.pickup ? (
                    <div className="space-y-2">
                      {selectedOrder.pickup.name && (
                        <p className="text-white font-medium">{selectedOrder.pickup.name}</p>
                      )}
                      {originAddress && <p className="text-foreground">{originAddress}</p>}
                      {selectedOrder.pickup.postcode && (
                        <p className="text-muted-foreground text-sm">Postcode: {selectedOrder.pickup.postcode}</p>
                      )}
                      {selectedOrder.pickup.date && (
                        <p className="text-muted-foreground text-sm">Date: {selectedOrder.pickup.date}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">—</p>
                  )}
                </div>

                <div className="bg-secondary/50 rounded-lg p-4 min-w-0">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-5 h-5 text-success shrink-0" />
                    <h4 className="text-lg font-semibold text-white">
                      {hideNameColumn ? 'Destination' : 'Delivery Location'}
                    </h4>
                  </div>
                  {selectedOrder.delivery ? (
                    <div className="space-y-2">
                      {selectedOrder.delivery.name && (
                        <p className="text-white font-medium">{selectedOrder.delivery.name}</p>
                      )}
                      {destinationAddress && <p className="text-foreground">{destinationAddress}</p>}
                      {selectedOrder.delivery.postcode && (
                        <p className="text-muted-foreground text-sm">Postcode: {selectedOrder.delivery.postcode}</p>
                      )}
                      {selectedOrder.delivery.date && (
                        <p className="text-muted-foreground text-sm">Date: {selectedOrder.delivery.date}</p>
                      )}
                      {selectedOrder.delivery.time && (
                        <p className="text-muted-foreground text-sm">Time: {selectedOrder.delivery.time}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">—</p>
                  )}
                </div>

                <div className="bg-secondary/50 rounded-lg p-4 min-w-0">
                  <div className="flex items-center gap-2 mb-3">
                    <Truck className="w-5 h-5 text-chart-5 shrink-0" />
                    <h4 className="text-lg font-semibold text-white">Goods Information</h4>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Weight className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div>
                        <p className="text-sm text-muted-foreground">Weight</p>
                        <p className="text-white font-semibold">
                          {selectedOrder.weightKg != null ? `${selectedOrder.weightKg.toFixed(1)} kg` : '—'}
                        </p>
                      </div>
                    </div>
                    {selectedOrder.volumeM3 != null && (
                      <div className="flex items-center gap-2">
                        <Box className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-sm text-muted-foreground">Volume</p>
                          <p className="text-white font-semibold">{selectedOrder.volumeM3.toFixed(2)} m³</p>
                        </div>
                      </div>
                    )}
                    {selectedOrder.lifts != null && (
                      <div className="flex items-center gap-2">
                        <Box className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-sm text-muted-foreground">Lifts</p>
                          <p className="text-white font-semibold">{selectedOrder.lifts}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Lifecycle — delivery / cancellation / arrival / custody facts, if any */}
              {(selectedOrder.podReference || selectedOrder.cancelledAt || selectedOrder.lastArrivedAt || selectedOrder.lastCustodyTransferAt) && (
                <div className="bg-secondary/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-5 h-5 text-chart-5 shrink-0" />
                    <h4 className="text-lg font-semibold text-white">Lifecycle</h4>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {selectedOrder.podReference && (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-success shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-muted-foreground">Proof of delivery</p>
                          <p className="text-white font-semibold truncate" title={selectedOrder.podReference}>
                            {selectedOrder.podReference}
                          </p>
                        </div>
                      </div>
                    )}
                    {selectedOrder.cancelledAt && (
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-destructive shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-muted-foreground">Cancelled</p>
                          <p className="text-white font-semibold">
                            {formatUkNumericDateTime(selectedOrder.cancelledAt)}
                            {selectedOrder.cancellationReason && selectedOrder.cancellationReason !== 'unknown'
                              ? ` — ${selectedOrder.cancellationReason.replace(/_/g, ' ')}`
                              : ''}
                          </p>
                        </div>
                      </div>
                    )}
                    {selectedOrder.lastArrivedAt && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-ring shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-muted-foreground">Last arrival at stop</p>
                          <p className="text-white font-semibold">{formatUkNumericDateTime(selectedOrder.lastArrivedAt)}</p>
                        </div>
                      </div>
                    )}
                    {selectedOrder.lastCustodyTransferAt && (
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-warning shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-muted-foreground">Last custody transfer</p>
                          <p className="text-white font-semibold">
                            {formatUkNumericDateTime(selectedOrder.lastCustodyTransferAt)}
                          </p>
                          {selectedOrder.custodyOperatorEntityId && (
                            <p className="text-muted-foreground text-xs truncate" title={selectedOrder.custodyOperatorEntityId}>
                              To: {selectedOrder.custodyOperatorEntityId}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Raw Data (if available) */}
              {selectedOrder._raw && (
                <div className="bg-secondary/50 rounded-lg p-4">
                  <h4 className="text-lg font-semibold text-white mb-3">Raw Data</h4>
                  <pre className="text-xs text-foreground overflow-x-auto bg-background/50 p-3 rounded">
                    {JSON.stringify(selectedOrder._raw, null, 2)}
                  </pre>
                </div>
              )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* KPI Detail Modal */}
      <KPIDataModal
        isOpen={kpiModal.open}
        onClose={() => {
          setKpiModal({ open: false, kpi: '', title: '' });
          onHighlightConsumed?.();
        }}
        title={kpiModal.title}
        subtitle={kpiModal.subtitle}
        icon={Package}
        data={processedOrders}
        columns={ORDER_COLUMNS}
        filterFn={kpiModal.filterFn}
        searchKeys={['id', 'name', 'customerRef', 'status', 'type', 'pickup.name', 'pickup.address', 'delivery.name', 'delivery.address']}
        exportFilename={`orders_${kpiModal.kpi}_${new Date().toISOString().split('T')[0]}.csv`}
      />
    </div>
  );
}
